import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { OnboardingImageFormComponent } from "./onboarding-image-form/onboarding-image-form.component";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { TooltipModule } from "primeng/tooltip";

import { BrandImagesService } from "../../../Utils/_services/admin/brand-images/brand-images.service";
import {
  IBrandImage,
  IBrandImageReq,
} from "../../../Utils/_models/brand-image.model";
import { ToastrService } from "ngx-toastr";
@Component({
  selector: "app-onboarding-images",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SpinnerLoaderComponent,
    OnboardingImageFormComponent,
    SearchBoxComponent,
    TooltipModule,
  ],
  templateUrl: "./onboarding-images.component.html",
  styleUrls: ["./onboarding-images.component.css"],
})
export class OnboardingImagesComponent implements OnInit {
  images = signal<IBrandImage[]>([]);
  loading = signal(false);
  isModalOpen = signal(false);
  hasSelection = signal(false);
  hasDeletedSelection = signal(false);
  isSaving = signal(false);
  editingImage = signal<IBrandImage | null>(null);
  previewPopupUrl = signal<string | null>(null);

  searchTerm = signal<string>("");
  includeDeleted = signal<boolean>(false);
  allSelected = signal<boolean>(false);
  selectedIds: Set<string> = new Set();

  currentPage = signal(1);
  totalCount = signal(0);
  pageSizes: number[] = [5, 10, 20, 50];
  pageSize = 5;
  totalPages = 0;
  startIndex = 1;
  endIndex = 5;
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  originalBrandImage = signal<IBrandImage[]>([]);
  constructor(
    private confirmationService: ConfirmService,
    private brandImagesService: BrandImagesService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.fetchImages();
  }

  fetchImages(): void {
    this.loading.set(true);
    const req: IBrandImageReq = {
      page: this.currentPage(),
      limit: this.pageSize,
      search: this.searchTerm() || undefined,
      includeDeleted: this.includeDeleted(),
    };

    this.brandImagesService.getAll(req).subscribe({
      next: res => {
        const mappedItems = (res?.data?.items || []).map((item: any) => ({
          ...item,
          imageUrl: item.image?.signedUrl || "",
          imageType: item.imageType?.toLowerCase() || "square",
        }));
        this.images.set(mappedItems);
        this.originalBrandImage.set([...mappedItems]);
        this.totalCount.set(res?.data?.total || 0);
        if (this.currentSortField() && this.currentSortOrder()) {
          this.sortBrandImageLocal();
        } else {
          this.updatePagination();
          this.loading.set(false);
          this.allSelected.set(false);
          this.hasSelection.set(false);
          this.selectedIds.clear();
        }
      },
      error: err => {
        this.toastr.error(err.message || "Failed to fetch brand images");
        this.loading.set(false);
      },
    });
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize);
    this.startIndex = (this.currentPage() - 1) * this.pageSize + 1;
    this.endIndex = Math.min(
      this.currentPage() * this.pageSize,
      this.totalCount()
    );
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.currentPage.set(1);
    this.fetchImages();
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.fetchImages();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.fetchImages();
    }
  }

  openAddModal(): void {
    this.editingImage.set(null);
    this.isModalOpen.set(true);
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
    this.currentPage.set(1);
    this.fetchImages();
  }

  toggleIncludeDeleted(): void {
    this.includeDeleted.set(!this.includeDeleted());
    this.fetchImages();
  }

  onToggleSelectAll(): void {
    const currentState = this.allSelected();
    this.allSelected.set(!currentState);

    if (!currentState) {
      this.images().forEach(img => {
        if (img._id) this.selectedIds.add(img._id);
      });
    } else {
      this.selectedIds.clear();
    }
    this.updateSelectionState();
  }

  onToggleSelect(img: any): void {
    if (this.selectedIds.has(img._id)) {
      this.selectedIds.delete(img._id);
    } else {
      this.selectedIds.add(img._id);
    }
    this.updateSelectionState();
  }

  updateSelectionState(): void {
    const all = this.images();
    this.allSelected.set(
      all.length > 0 &&
        all.every(img => img._id && this.selectedIds.has(img._id))
    );
    this.hasSelection.set(this.selectedIds.size > 0);
    this.hasDeletedSelection.set(
      all.some(
        img => img._id && this.selectedIds.has(img._id) && !!img.deletedAt
      )
    );
  }

  isSelected(img: any): boolean {
    return this.selectedIds.has(img._id);
  }

  async deleteSelectedImages(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const all = this.images();
    const selectedImages = all.filter(
      img => img._id && this.selectedIds.has(img._id)
    );
    const deletedSelected = selectedImages.filter(img => img.deletedAt);
    const activeSelected = selectedImages.filter(img => !img.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? "Restore Images" : "Delete Images";
    const bodyHeader = isRestore
      ? "Restore Selected Images"
      : "Delete Selected Images";
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted image(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted image(s) will also be deleted.` : ""}`
      : `Are you sure you want to delete ${activeSelected.length} selected image(s)? This action cannot be undone.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? "Apply Changes"
        : isRestore
          ? "Restore"
          : "Delete";
    const yesBtnCss = isRestore ? "btn-primary" : "btn-danger";

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      noButtonText: "Cancel",
      yesBtnCss,
      noBtnCss: "btn-secondary",
      width: "420px",
    });

    if (confirmed) {
      const ids = Array.from(this.selectedIds);
      this.brandImagesService.bulkDeleteBrandImages(ids).subscribe({
        next: () => {
          this.toastr.success("Images deleted successfully");
          this.selectedIds.clear();
          this.updateSelectionState();
          this.fetchImages();
        },
        error: err => {
          this.toastr.error(
            err?.ApiError?.msg || err.message || "Failed to delete images"
          );
        },
      });
    }
  }

  openEditModal(img: IBrandImage): void {
    this.editingImage.set(img);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.editingImage.set(null);
  }

  onFormSaved(payload: {
    name: string;
    age: number;
    message: string;
    category: "onboarding" | "auth" | "landing" | "advertisment" | "";
    imageType: "circle" | "square";
    croppedBlob: Blob | null;
    croppedPreviewUrl: string;
    weight?: string;
    order?: number;
  }): void {
    const editing = this.editingImage();

    // Construct the request body
    const reqData: any = {
      name: payload.name,
      age: payload.age,
      message: payload.message,
      category: payload.category,
      imageType: payload.imageType === "circle" ? "Circle" : "Square",
      isActive: editing ? editing.isActive : true,
      weight: payload.weight,
      order: payload.order,
      image: payload.croppedBlob
        ? new File([payload.croppedBlob], "image.png", { type: "image/png" })
        : null,
    };

    this.isSaving.set(true);

    if (editing) {
      reqData.id = editing._id;
      this.brandImagesService.updateBrandImage(reqData).subscribe({
        next: () => {
          this.toastr.success("Brand image updated successfully");
          this.fetchImages();
          this.closeModal();
          this.isSaving.set(false);
        },
        error: err => {
          this.toastr.error(err.message || "Failed to update image");
          this.isSaving.set(false);
        },
      });
    } else {
      this.brandImagesService.createBrandImage(reqData).subscribe({
        next: () => {
          this.toastr.success("Brand image created successfully");
          this.fetchImages();
          this.closeModal();
          this.isSaving.set(false);
        },
        error: err => {
          this.toastr.error(err.message || "Failed to create image");
          this.isSaving.set(false);
        },
      });
    }
  }

  toggleStatus(img: IBrandImage): void {
    const newStatus = !img.isActive;
    this.brandImagesService
      .updateBrandImage({ id: img._id, isActive: newStatus })
      .subscribe({
        next: () => {
          this.toastr.success("Status updated");
          img.isActive = newStatus;
        },
        error: err => {
          this.toastr.error(err.message || "Failed to update status");
        },
      });
  }

  async deleteImage(img: IBrandImage): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      header: "",
      bodyHeader: "Deleting image",
      bodySubtitle:
        "Are you sure you want to delete this image? This action cannot be undone.",
      yesButtonText: "Delete",
      yesBtnCss: "btn-danger",
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });
    if (confirmed) {
      this.brandImagesService.bulkDeleteBrandImages([img._id]).subscribe({
        next: () => {
          this.toastr.success("Image deleted successfully");
          this.fetchImages();
        },
        error: err => {
          this.toastr.error(
            err?.ApiError?.msg || err.message || "Failed to delete image"
          );
        },
      });
    }
  }

  openPreview(url: string): void {
    this.previewPopupUrl.set(url);
  }
  closePreview(): void {
    this.previewPopupUrl.set(null);
  }
  trackById(_: number, item: IBrandImage): string {
    return item._id;
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortBrandImageLocal();
  }

  sortBrandImageLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.images.set([...this.originalBrandImage()]);
      this.updatePagination();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.images()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === "name") {
        valA = (a.name || "").toLowerCase();
        valB = (b.name || "").toLowerCase();
      } else if (field === "age") {
        valA = Number(a.age || 0);
        valB = Number(b.age || 0);
      } else if (field === "category") {
        valA = (a.category || "").toLowerCase();
        valB = (b.category || "").toLowerCase();
      } else if (field === "order") {
        valA = Number(a.order || 0);
        valB = Number(b.order || 0);
      } else if (field === "weight") {
        valA = (a.weight || "").toLowerCase();
        valB = (b.weight || "").toLowerCase();
      } else if (field === "status") {
        valA = a.isActive ? 1 : 0;
        valB = b.isActive ? 1 : 0;
      } else if (field === "imageType") {
        valA = (a.imageType || "").toLowerCase();
        valB = (b.imageType || "").toLowerCase();
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.images.set(sorted);
    this.updatePagination();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}
